import { ImageAnalysisSample, MLS, MLSID, schAddress } from '@aloft/toolkit-shared';
import {
  Block,
  Button,
  Checkbox,
  Flex,
  SimpleInput,
  SimpleSelect,
  Typography,
} from '@aloft/toolkit-sui';
import { Textarea } from '@aloft/toolkit-sui/chakra';

import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import ReactMarkdown from 'react-markdown';

import { AddressInput } from '../../../../components/AddressInput/AddressInput.tsx';
import {
  AnalysisMode,
  CommonFields,
  DEFAULT_MODEL,
  DEFAULT_PROMPTS,
  GEMINI_MODELS,
  ImageAnalysisFormSubmit,
} from './imageAnalysisFormConfig.ts';

export type { AnalysisMode, ImageAnalysisFormSubmit };

type LookupType = 'mlsId' | 'address';
type CostToCureInputMode = 'toolkitOrder' | 'manual';

const SORTED_ACTIVE_MLS_KEYS = (MLS._keys.filter((key) => MLS[key].isActive) as MLSID[]).sort(
  (a, b) => (MLS[a].shortName ?? a).localeCompare(MLS[b].shortName ?? b),
);

const COST_TO_CURE_INPUT_MODE_HELP: Record<CostToCureInputMode, string> = {
  toolkitOrder:
    'The system will obtain the report, cost to cure, condition rating and commentary from the provided completed order.',
  manual:
    'The system will obtain report photos from the report ID and use the cost to cure, condition rating and commentary you provide below.',
};

const COMMENTARY_REVIEW_HELP =
  'The system will load the completed report data from the order and review its commentary for unsupported, contradictory, or incomplete fields.';

type Props = {
  onSubmit: (values: ImageAnalysisFormSubmit) => void;
  isSubmitting: boolean;
  analysisMode: AnalysisMode;
};

export const ImageAnalysisForm = ({ onSubmit, isSubmitting, analysisMode }: Props) => {
  const [lookupType, setLookupType] = useState<LookupType>('address');
  const [costToCureInputMode, setCostToCureInputMode] =
    useState<CostToCureInputMode>('toolkitOrder');
  const [mlsid, setMlsid] = useState<MLSID>(SORTED_ACTIVE_MLS_KEYS[0]!);
  const [listingID, setListingID] = useState('');
  const [orderID, setOrderID] = useState('');
  const [reportId, setReportId] = useState('');
  const [costToCureAmount, setCostToCureAmount] = useState('');
  const [conditionRating, setConditionRating] = useState('');
  const [commentary, setCommentary] = useState('');
  const [model, setModel] = useState<string>(DEFAULT_MODEL);
  const [promptOverride, setPromptOverride] = useState(DEFAULT_PROMPTS['uadRatings']);
  const [overrideEnabled, setOverrideEnabled] = useState(false);
  const [sampleType, setSampleType] = useState<'all' | 'first' | 'random'>('all');
  const [sampleCount, setSampleCount] = useState('10');
  const [compress, setCompress] = useState(true);

  const addressForm = useForm({
    defaultValues: {
      address: {
        AddressLineText: '',
        unitNumber: '',
        CityName: '',
        StateCode: '',
        PostalCode: '',
      },
    },
  });

  const watchedAddress = addressForm.watch('address');

  useEffect(() => {
    setPromptOverride(DEFAULT_PROMPTS[analysisMode]);
    setOverrideEnabled(false);
  }, [analysisMode]);

  const handleSubmit = () => {
    const count = Math.max(1, parseInt(sampleCount, 10) || 10);
    let sample: ImageAnalysisSample;
    if (sampleType === 'random') {
      sample = { type: 'random', count };
    } else if (sampleType === 'first') {
      sample = { type: 'first', count };
    } else {
      sample = { type: 'all' };
    }

    const common = {
      model,
      promptOverride: overrideEnabled ? promptOverride : undefined,
      sample,
      compress,
      submittedLabel: '',
    };

    if (analysisMode === 'commentaryReview') {
      onSubmit({
        analysisMode,
        model,
        promptOverride: overrideEnabled ? promptOverride : undefined,
        submittedLabel: `Toolkit Order ${orderID.trim()}`,
        orderID: orderID.trim(),
      });
      return;
    }

    if (analysisMode === 'costToCure') {
      const costToCureCommon: CommonFields & { analysisMode: 'costToCure' } = {
        ...common,
        analysisMode,
      };
      if (costToCureInputMode === 'toolkitOrder') {
        onSubmit({
          ...costToCureCommon,
          inputMode: 'toolkitOrder',
          orderID: orderID.trim(),
          submittedLabel: `Toolkit Order ${orderID.trim()}`,
        });
        return;
      }

      const parsedCostToCureAmount = Number.parseFloat(costToCureAmount.replace(/,/g, ''));
      onSubmit({
        ...costToCureCommon,
        inputMode: 'manual',
        reportId: reportId.trim(),
        appraisal: {
          costToCureAmount: Number.isFinite(parsedCostToCureAmount)
            ? parsedCostToCureAmount
            : undefined,
          conditionRating: conditionRating || undefined,
          commentary: commentary.trim() || undefined,
        },
        submittedLabel: `Report ${reportId.trim()}`,
      });
      return;
    }

    const adminAnalysisMode: 'craftsmanQuality' | 'uadRatings' = analysisMode;
    if (lookupType === 'mlsId') {
      onSubmit({
        ...common,
        analysisMode: adminAnalysisMode,
        lookupType: 'mlsId',
        mlsid,
        listingID: listingID.trim(),
        submittedLabel: `${MLS[mlsid].shortName ?? mlsid} #${listingID.trim()}`,
      });
    } else {
      const address = schAddress.parse(watchedAddress ?? {});
      const { AddressLineText, CityName, StateCode } = watchedAddress ?? {};
      onSubmit({
        ...common,
        analysisMode: adminAnalysisMode,
        lookupType: 'address',
        address,
        submittedLabel: [AddressLineText, CityName, StateCode].filter(Boolean).join(', '),
      });
    }
  };

  const isSubmitDisabled =
    isSubmitting ||
    (analysisMode === 'costToCure'
      ? costToCureInputMode === 'toolkitOrder'
        ? orderID.trim() === ''
        : reportId.trim() === ''
      : analysisMode === 'commentaryReview'
        ? orderID.trim() === ''
      : (lookupType === 'mlsId' && listingID.trim() === '') ||
        (lookupType === 'address' && !watchedAddress?.AddressLineText));

  return (
    <Flex gap="24px" flexDirection="column">
      {/* Lookup type + model */}
      <Flex gap="16px" flexWrap="wrap" alignItems="flex-end">
        {analysisMode === 'costToCure' ? (
          <Flex flexDirection="column" gap="6px">
            <SimpleSelect
              label="Source"
              value={costToCureInputMode}
              onChange={(e) => setCostToCureInputMode(e.target.value as CostToCureInputMode)}
              width="180px"
              size="sm"
            >
              <option value="toolkitOrder">Toolkit order</option>
              <option value="manual">Manual report</option>
            </SimpleSelect>
            <Typography variant="body2" color="textSecondary">
              {COST_TO_CURE_INPUT_MODE_HELP[costToCureInputMode]}
            </Typography>
          </Flex>
        ) : analysisMode === 'commentaryReview' ? (
          <Flex flexDirection="column" gap="6px">
            <Typography variant="h4">Toolkit order</Typography>
            <Typography variant="body2" color="textSecondary">
              {COMMENTARY_REVIEW_HELP}
            </Typography>
          </Flex>
        ) : (
          <SimpleSelect
            label="Lookup by"
            value={lookupType}
            onChange={(e) => setLookupType(e.target.value as LookupType)}
            width="160px"
            size="sm"
          >
            <option value="mlsId">MLS ID</option>
            <option value="address">Address</option>
          </SimpleSelect>
        )}

        <SimpleSelect
          label="Model"
          value={model}
          onChange={(e) => setModel(e.target.value)}
          width="300px"
          size="sm"
        >
          {GEMINI_MODELS.map((m) => (
            <option key={m.id} value={m.id}>
              {m.label}
            </option>
          ))}
        </SimpleSelect>
      </Flex>

      {/* Cost-to-Cure inputs */}
      {analysisMode === 'costToCure' && costToCureInputMode === 'toolkitOrder' && (
        <Flex gap="16px" flexWrap="wrap" alignItems="flex-end">
          <SimpleInput
            label="Toolkit Order ID"
            value={orderID}
            onChange={(e) => setOrderID(e.target.value)}
            size="sm"
            width="420px"
            placeholder="e.g. 64315b17-8187-46c4-b569-39dc9a718161"
          />
        </Flex>
      )}

      {analysisMode === 'commentaryReview' && (
        <Flex gap="16px" flexWrap="wrap" alignItems="flex-end">
          <SimpleInput
            label="Toolkit Order ID"
            value={orderID}
            onChange={(e) => setOrderID(e.target.value)}
            size="sm"
            width="420px"
            placeholder="e.g. 64315b17-8187-46c4-b569-39dc9a718161"
          />
        </Flex>
      )}

      {analysisMode === 'costToCure' && costToCureInputMode === 'manual' && (
        <Flex gap="16px" flexDirection="column">
          <Flex gap="16px" flexWrap="wrap" alignItems="flex-end">
            <SimpleInput
              label="Report ID"
              value={reportId}
              onChange={(e) => setReportId(e.target.value)}
              size="sm"
              width="420px"
              placeholder="e.g. 64315b17-8187-46c4-b569-39dc9a718161"
            />
            <SimpleInput
              label="Cost-to-Cure Amount"
              value={costToCureAmount}
              onChange={(e) => setCostToCureAmount(e.target.value)}
              size="sm"
              width="180px"
              placeholder="e.g. 5000"
            />
            <SimpleSelect
              label="Condition Rating"
              value={conditionRating}
              onChange={(e) => setConditionRating(e.target.value)}
              width="180px"
              size="sm"
            >
              <option value="">Not provided</option>
              <option value="C1">C1</option>
              <option value="C2">C2</option>
              <option value="C3">C3</option>
              <option value="C4">C4</option>
              <option value="C5">C5</option>
              <option value="C6">C6</option>
            </SimpleSelect>
          </Flex>
          <Textarea
            value={commentary}
            onChange={(e) => setCommentary(e.target.value)}
            placeholder="Cost-to-Cure or condition commentary"
            style={{ width: '100%', minHeight: '120px', resize: 'vertical', padding: '8px' }}
          />
        </Flex>
      )}

      {/* MLS ID inputs */}
      {analysisMode !== 'costToCure' && analysisMode !== 'commentaryReview' && lookupType === 'mlsId' && (
        <Flex gap="16px" flexWrap="wrap" alignItems="flex-end">
          <SimpleSelect
            label="MLS"
            value={mlsid}
            onChange={(e) => setMlsid(e.target.value as MLSID)}
            width="400px"
            size="sm"
          >
            {SORTED_ACTIVE_MLS_KEYS.map((key) => (
              <option key={key} value={key}>
                {MLS[key].shortName ?? key}
              </option>
            ))}
          </SimpleSelect>

          <SimpleInput
            label="Listing ID"
            value={listingID}
            onChange={(e) => setListingID(e.target.value)}
            size="sm"
            width="200px"
            placeholder="e.g. 12345678"
          />
        </Flex>
      )}

      {/* Address input */}
      {analysisMode !== 'costToCure' && analysisMode !== 'commentaryReview' && lookupType === 'address' && (
        <FormProvider {...addressForm}>
          <AddressInput addressLabel="Property Address" isRequired={false} />
        </FormProvider>
      )}

      {/* Image sample */}
      {analysisMode !== 'commentaryReview' && <Flex gap="16px" flexWrap="wrap" alignItems="flex-end">
        <SimpleSelect
          label="Images"
          value={sampleType}
          onChange={(e) => setSampleType(e.target.value as 'all' | 'first' | 'random')}
          width="160px"
          size="sm"
        >
          <option value="all">All images</option>
          <option value="first">First N</option>
          <option value="random">Random sample</option>
        </SimpleSelect>

        {(sampleType === 'random' || sampleType === 'first') && (
          <SimpleInput
            label="Count"
            value={sampleCount}
            onChange={(e) => setSampleCount(e.target.value)}
            size="sm"
            width="80px"
            placeholder="10"
          />
        )}
        {sampleType === 'random' && (
          <Typography variant="body2" color="textSecondary">
            First 3 photos always included
          </Typography>
        )}
        <Checkbox isChecked={compress} onChange={(e) => setCompress(e.target.checked)}>
          Compress images
        </Checkbox>
      </Flex>}

      <Flex>
        <Button text="Run Analysis" onClick={handleSubmit} disabled={isSubmitDisabled} size="sm" />
      </Flex>

      {/* Prompt */}
      <Flex flexDirection="column" gap="8px">
        <Flex alignItems="center" gap="12px">
          <Typography variant="h3">System Prompt</Typography>
          <Checkbox
            isChecked={overrideEnabled}
            onChange={(e) => setOverrideEnabled(e.target.checked)}
          >
            Override
          </Checkbox>
        </Flex>

        {overrideEnabled ? (
          <Textarea
            value={promptOverride}
            onChange={(e) => setPromptOverride(e.target.value)}
            style={{
              width: '100%',
              minHeight: '600px',
              resize: 'vertical',
              fontSize: '12px',
              fontFamily: 'monospace',
              padding: '8px',
            }}
          />
        ) : (
          <Block
            padding="16px"
            borderRadius="4px"
            backgroundColor="surface2"
            overflow="auto"
            maxHeight="600px"
          >
            <ReactMarkdown>{DEFAULT_PROMPTS[analysisMode]}</ReactMarkdown>
          </Block>
        )}
      </Flex>
    </Flex>
  );
};
